import { useCallback, useEffect, useRef, useState } from 'react';
import useFollowerStore from '@/stores/FollowerStore';
import useUserStore from '@/stores/UserStore';
import { Loader2 } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import {
  fetchHistoryFollowersForOpens,
  fetchSearchFollowersForOpens,
  processFollowerAction,
} from '@/hooks/follower';

import { ScrollArea } from '../ui/scroll-area';
import FollowerBulkActions from './FollowerBulkActions';
import FollowerCard from './FollowerCard';
import FollowerEmptyState from './FollowerEmptyState';
import FollowerErrorState from './FollowerErrorState';
// Import the new smaller components
import FollowerFilters from './FollowerFilters';
import FollowerLoadingState from './FollowerLoadingState';
import HistoryFilters from './HistoryFilters';

const FollowerList = () => {
  const { ref: inViewRef, inView } = useInView();
  const navigate = useNavigate();

  const { personalizeOpensEnabled, selfServiceOpensEnabled } = useUserStore(
    (state) => ({
      personalizeOpensEnabled: state.personalizeOpensEnabled,
      selfServiceOpensEnabled: state.selfServiceOpensEnabled,
    })
  );

  const {
    activeFilter,
    searchQuery,
    advancedFilters,
    followers,
    followersLoading,
    followersError,
    page,
    followerCount,
    totals,
    historyDate,
    historyActionType,
    historySummary,
    setActiveFilter,
    setSearchQuery,
    setFollowers,
    setNextFollowers,
    setFollowersLoading,
    setFollowersError,
    setTotals,
    setHistoryDate,
    setHistoryActionType,
    setHistorySummary,
    updateTotalsOnAction,
    updatePersonalizedOpening,
    updateFollowersFromResponse,
    removeFollowersFromStore,
    getFilteredFollowers,
    setSearchFollower,
  } = useFollowerStore(
    useShallow((state) => ({
      activeFilter: state.activeFilter,
      searchQuery: state.searchQuery,
      advancedFilters: state.advancedFilters,
      followers: state.followers,
      followersLoading: state.followersLoading,
      followersError: state.followersError,
      page: state.page,
      followerCount: state.followerCount,
      totals: state.totals,
      historyDate: state.historyDate,
      historyActionType: state.historyActionType,
      historySummary: state.historySummary,
      setActiveFilter: state.setActiveFilter,
      setSearchQuery: state.setSearchQuery,
      setFollowers: state.setFollowers,
      setNextFollowers: state.setNextFollowers,
      setFollowersLoading: state.setFollowersLoading,
      setFollowersError: state.setFollowersError,
      setTotals: state.setTotals,
      setHistoryDate: state.setHistoryDate,
      setHistoryActionType: state.setHistoryActionType,
      setHistorySummary: state.setHistorySummary,
      updateTotalsOnAction: state.updateTotalsOnAction,
      updatePersonalizedOpening: state.updatePersonalizedOpening,
      updateFollowersFromResponse: state.updateFollowersFromResponse,
      removeFollowersFromStore: state.removeFollowersFromStore,
      getFilteredFollowers: state.getFilteredFollowers,
      setSearchFollower: state.setSearchFollower,
    }))
  );

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedFollowers, setSelectedFollowers] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [removingFollowers, setRemovingFollowers] = useState(new Set());
  const [loadingFollowers, setLoadingFollowers] = useState(new Map()); // Changed to Map to track action type
  const [filterLoading, setFilterLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(null); // 'approve' or 'reject' or null
  const scrollAreaRef = useRef(null);

  // Fetch followers with proper loading and error handling
  const fetchData = useCallback(
    async (type, page, activeFilter, searchQuery, advancedFilters) => {
      try {
        setFollowersLoading(true);
        setFollowersError(null);

        // Set filter loading for new data fetches
        if (type === 'new') {
          setFilterLoading(true);
        }

        let response;

        // Handle history filter differently
        if (activeFilter === 'history') {
          response = await fetchHistoryFollowersForOpens(
            page,
            historyDate,
            historyActionType
          );
        } else {
          // Build combined filters (advanced + status + username) → OR of groups, AND within a group
          let combinedFilters = Array.isArray(advancedFilters)
            ? advancedFilters.map((group) =>
                Array.isArray(group) ? [...group] : []
              )
            : [];

          const andIntoFirstGroup = (condition) => {
            if (!condition) return;
            if (
              !Array.isArray(combinedFilters) ||
              combinedFilters.length === 0
            ) {
              combinedFilters = [[condition]];
            } else {
              combinedFilters[0] = [...combinedFilters[0], condition];
            }
          };

          // personalized_action_status from activeFilter (except 'all')
          if (
            activeFilter === 'approved' ||
            activeFilter === 'rejected' ||
            activeFilter === 'pending'
          ) {
            andIntoFirstGroup({
              field: 'personalized_action_status',
              operator: 'contains_exactly',
              value: activeFilter,
            });
          }

          // username search
          const trimmedSearch =
            typeof searchQuery === 'string' ? searchQuery.trim() : '';
          if (trimmedSearch) {
            andIntoFirstGroup({
              field: 'username',
              operator: 'contains_exactly',
              value: trimmedSearch,
            });
          }

          const params =
            combinedFilters.length > 0 ? { filters: combinedFilters } : {};

          response = await fetchSearchFollowersForOpens(page, params);
        }

        if (type === 'new') {
          // Initial load - replace all followers
          const {
            data,
            next_page_url,
            current_page,
            total,
            totals,
            history_summary,
          } = response;
          setFollowers(data, next_page_url ? current_page + 1 : null, total);
          setSearchFollower(response);

          // Set totals from API response
          if (totals) {
            setTotals(totals);
          }

          // Set history summary if available
          if (history_summary) {
            setHistorySummary(history_summary);
          }
        } else {
          // Load more - append followers
          const { data, next_page_url, current_page, total } = response;
          setNextFollowers(
            data,
            next_page_url ? current_page + 1 : null,
            total
          );
        }

        setFollowersLoading(false);
        setFilterLoading(false);
      } catch (error) {
        console.error('Error fetching followers:', error);
        setFollowersError(error.message || 'Failed to fetch followers');
        setFollowersLoading(false);
        setFilterLoading(false);
      }
    },
    [
      setFollowersLoading,
      setFollowersError,
      setFollowers,
      setNextFollowers,
      setSearchFollower,
      setTotals,
      setHistorySummary,
      historyDate,
      historyActionType,
    ]
  );

  useEffect(() => {
    if (personalizeOpensEnabled === false) {
      navigate('/inbox');
    }
    if (selfServiceOpensEnabled === true) {
      navigate('/followers/self-service/opens');
    }
  }, [personalizeOpensEnabled, selfServiceOpensEnabled, navigate]);

  // Initial load
  useEffect(() => {
    fetchData('new', 1, activeFilter, searchQuery, advancedFilters);
  }, [fetchData]);

  // Reset and reload when filters change
  useEffect(() => {
    fetchData('new', 1, activeFilter, searchQuery, advancedFilters);
  }, [
    activeFilter,
    searchQuery,
    advancedFilters,
    historyDate,
    historyActionType,
    fetchData,
  ]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (inView) {
      console.log('in view');
      fetchData('next', page, activeFilter, searchQuery, advancedFilters);
    }
  }, [inView, fetchData]);

  // If multi select mode is enabled, reset the selected followers on filter change
  useEffect(() => {
    if (isMultiSelectMode) {
      setSelectedFollowers(new Set());
    }
  }, [isMultiSelectMode, activeFilter]);

  if (!personalizeOpensEnabled) {
    return null;
  }

  const filteredFollowers = getFilteredFollowers();
  const selectedCount = selectedFollowers.size;

  const handleEdit = (follower, value) => {
    setEditingId(follower._id);
    setEditValue(value || follower.opens_message?.text || '');
  };

  const handleSave = (followerId) => {
    updatePersonalizedOpening(followerId, editValue);

    setEditingId(null);
    setEditValue('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleBulkApprove = () => {
    const selectedFollowerIds = Array.from(selectedFollowers);
    const selectedFollowerData = selectedFollowerIds.map((followerId) => {
      const follower = followers.find((f) => f._id === followerId);
      return {
        follower_id: followerId,
        text: follower?.opens_message?.text || '',
      };
    });

    // Collect previous statuses for totals update
    const previousStatuses = {};
    selectedFollowerIds.forEach((followerId) => {
      const follower = followers.find((f) => f._id === followerId);
      previousStatuses[followerId] =
        follower?.personalized_action_status || 'pending';
    });

    // Set loading state for all selected followers
    setLoadingFollowers(
      new Map(selectedFollowerIds.map((id) => [id, 'approve']))
    );
    // Set bulk loading state
    setBulkLoading('approve');

    toast.promise(processFollowerAction('approve', selectedFollowerData), {
      loading: `Processing ${selectedFollowerIds.length} followers...`,
      success: (response) => {
        const { processed_ids, unprocessed_ids } = response;

        // Clear selections
        setSelectedFollowers(new Set());

        // Clear loading state
        setLoadingFollowers(new Map());
        setBulkLoading(null);

        // Remove unprocessed id from followers
        if (unprocessed_ids.length > 0) {
          removeFollowersFromStore(unprocessed_ids);
        }

        if (processed_ids.length > 0) {
          // Handle differently based on filter
          if (activeFilter !== 'all') {
            // For specific filters, use removal animation
            setRemovingFollowers(new Set(processed_ids));

            // Use setTimeout to allow animation to play before removing from store
            setTimeout(() => {
              removeFollowersFromStore(processed_ids);
              setRemovingFollowers(new Set());
            }, 300); // Match the CSS transition duration
          } else {
            // For "all" filter, immediately update status without animation
            updateFollowersFromResponse(
              processed_ids,
              unprocessed_ids,
              'approved'
            );
          }

          // Update totals with previous statuses
          updateTotalsOnAction(processed_ids, 'approve', previousStatuses);
        }

        // Show success message
        if (processed_ids.length > 0) {
          if (unprocessed_ids.length > 0) {
            return `Approved ${processed_ids.length} followers. ${unprocessed_ids.length} failed.`;
          }
          return `Successfully approved ${processed_ids.length} followers!`;
        } else {
          return 'No followers were approved.';
        }
      },
      error: (error) => {
        // Clear loading state on error
        setLoadingFollowers(new Map());
        setBulkLoading(null);
        console.error('Error processing bulk approve:', error);
        const serverMessage = error.response?.data?.message;
        return serverMessage || 'Failed to approve followers';
      },
    });
  };

  const handleBulkReject = () => {
    const selectedFollowerIds = Array.from(selectedFollowers);
    const selectedFollowerData = selectedFollowerIds.map((followerId) => {
      return {
        follower_id: followerId,
      };
    });

    // Collect previous statuses for totals update
    const previousStatuses = {};
    selectedFollowerIds.forEach((followerId) => {
      const follower = followers.find((f) => f._id === followerId);
      previousStatuses[followerId] =
        follower?.personalized_action_status || 'pending';
    });

    // Set loading state for all selected followers
    setLoadingFollowers(
      new Map(selectedFollowerIds.map((id) => [id, 'reject']))
    );
    // Set bulk loading state
    setBulkLoading('reject');

    toast.promise(processFollowerAction('reject', selectedFollowerData), {
      loading: `Processing ${selectedFollowerIds.length} followers...`,
      success: (response) => {
        const { processed_ids, unprocessed_ids } = response;

        // Clear selections
        setSelectedFollowers(new Set());

        // Clear loading state
        setLoadingFollowers(new Map());
        setBulkLoading(null);

        // Remove unprocessed id from followers
        if (unprocessed_ids.length > 0) {
          removeFollowersFromStore(unprocessed_ids);
        }

        if (processed_ids.length > 0) {
          // Handle differently based on filter
          if (activeFilter !== 'all') {
            // For specific filters, use removal animation
            setRemovingFollowers(new Set(processed_ids));

            // Use setTimeout to allow animation to play before removing from store
            setTimeout(() => {
              removeFollowersFromStore(processed_ids);
              setRemovingFollowers(new Set());
            }, 300); // Match the CSS transition duration
          } else {
            // For "all" filter, immediately update status without animation
            updateFollowersFromResponse(
              processed_ids,
              unprocessed_ids,
              'rejected'
            );
          }

          // Update totals with previous statuses
          updateTotalsOnAction(processed_ids, 'reject', previousStatuses);
        }

        // Show success message
        if (processed_ids.length > 0) {
          if (unprocessed_ids.length > 0) {
            return `Rejected ${processed_ids.length} followers. ${unprocessed_ids.length} failed.`;
          }
          return `Successfully rejected ${processed_ids.length} followers!`;
        } else {
          return 'No followers were rejected.';
        }
      },
      error: (error) => {
        // Clear loading state on error
        setLoadingFollowers(new Map());
        setBulkLoading(null);
        console.error('Error processing bulk reject:', error);
        const serverMessage = error.response?.data?.message;
        return serverMessage || 'Failed to reject followers';
      },
    });
  };

  const handleStatusChange = (followerId, newStatus) => {
    const follower = followers.find((f) => f._id === followerId);
    const followerData = [
      newStatus === 'approve'
        ? {
            follower_id: followerId,
            text: follower?.opens_message?.text || '',
          }
        : {
            follower_id: followerId,
          },
    ];

    // Get previous status for totals update
    const previousStatus = follower?.personalized_action_status || 'pending';

    // Set loading state when button is clicked
    setLoadingFollowers(new Map([[followerId, newStatus]]));

    toast.promise(processFollowerAction(newStatus, followerData), {
      loading: `${
        newStatus === 'approve' ? 'Approving' : 'Rejecting'
      } follower...`,
      success: (response) => {
        const { processed_ids, unprocessed_ids } = response;

        // Remove unprocessed id from followers
        if (unprocessed_ids.length > 0) {
          removeFollowersFromStore(unprocessed_ids);
        }

        if (processed_ids.includes(followerId)) {
          // Clear loading state
          setLoadingFollowers(new Map());

          // Handle differently based on filter
          if (activeFilter !== 'all') {
            // For specific filters, use removal animation
            setRemovingFollowers(new Set([followerId]));

            // Use setTimeout to allow animation to play before removing from store
            setTimeout(() => {
              removeFollowersFromStore(processed_ids);
              setRemovingFollowers(new Set());
            }, 300); // Match the CSS transition duration
          } else {
            // For "all" filter, immediately update status without animation
            const statusToUpdate =
              newStatus === 'approve' ? 'approved' : 'rejected';
            updateFollowersFromResponse(
              processed_ids,
              unprocessed_ids,
              statusToUpdate
            );
          }

          // Update totals with previous status
          updateTotalsOnAction(processed_ids, newStatus, {
            [followerId]: previousStatus,
          });

          return `Successfully ${
            newStatus === 'approve' ? 'approved' : 'rejected'
          } follower!`;
        } else {
          // Clear loading state on failure
          setLoadingFollowers(new Map());
          // Show error for unprocessed follower
          const reason = 'Unknown error';
          return `Failed to ${
            newStatus === 'approve' ? 'approve' : 'reject'
          } follower: ${reason}`;
        }
      },
      error: (error) => {
        // Clear loading state on error
        setLoadingFollowers(new Map());
        console.error(
          `Error ${
            newStatus === 'approve' ? 'approving' : 'rejecting'
          } follower:`,
          error
        );
        const serverMessage = error.response?.data?.message;
        return (
          serverMessage ||
          `Failed to ${newStatus === 'approve' ? 'approve' : 'reject'} follower`
        );
      },
    });
  };

  const handleSelectFollower = (followerId) => {
    const newSelected = new Set(selectedFollowers);
    if (newSelected.has(followerId)) {
      newSelected.delete(followerId);
    } else {
      newSelected.add(followerId);
    }
    setSelectedFollowers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCount === filteredFollowers.length) {
      setSelectedFollowers(new Set());
    } else {
      setSelectedFollowers(new Set(filteredFollowers.map((f) => f._id)));
    }
  };

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    setSelectedFollowers(new Set()); // Clear selections when toggling
  };

  // Loading state
  if (followersLoading && followers.length === 0) {
    return <FollowerLoadingState />;
  }

  // Filter loading state - show when changing filters but we have existing data
  if (filterLoading && followers.length > 0) {
    return (
      <div className='flex h-full flex-col'>
        {/* Fixed Filter and Selection Bar */}
        <div className='p-5'>
          <div className='flex-shrink-0 space-y-4 rounded-lg bg-muted/50 p-4'>
            {/* Top Row: Filters */}
            {activeFilter === 'history' ? (
              <HistoryFilters
                historyDate={historyDate}
                historyActionType={historyActionType}
                historySummary={historySummary}
                onDateChange={setHistoryDate}
                onActionTypeChange={setHistoryActionType}
                onNavigateToPending={() => setActiveFilter('pending')}
                isLoading={followersLoading || filterLoading}
              />
            ) : (
              <FollowerFilters
                activeFilter={activeFilter}
                searchQuery={searchQuery}
                isMultiSelectMode={isMultiSelectMode}
                filteredFollowersLength={filteredFollowers.length}
                totals={totals}
                onFilterChange={setActiveFilter}
                onSearchChange={setSearchQuery}
                onMultiSelectToggle={toggleMultiSelectMode}
                isLoading={followersLoading || filterLoading}
              />
            )}

            {/* Bottom Row: Bulk Actions */}
            <FollowerBulkActions
              isMultiSelectMode={isMultiSelectMode}
              selectedCount={selectedCount}
              totalFollowersCount={followerCount}
              currentPageFollowersCount={filteredFollowers.length}
              bulkLoading={bulkLoading}
              activeFilter={activeFilter}
              onSelectAll={handleSelectAll}
              onBulkApprove={handleBulkApprove}
              onBulkReject={handleBulkReject}
            />
          </div>
        </div>

        {/* Loading Overlay */}
        <div className='relative mt-4 min-h-0 flex-1'>
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-sm'>
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span>
                Loading {activeFilter === 'all' ? 'all' : activeFilter}{' '}
                followers...
              </span>
            </div>
          </div>

          {/* Existing content with reduced opacity */}
          <div className='pointer-events-none opacity-50'>
            <ScrollArea ref={scrollAreaRef} className='h-full'>
              <div className='pl-1 pr-4 pt-1'>
                {filteredFollowers.length === 0 ? (
                  <FollowerEmptyState />
                ) : (
                  <>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
                      {filteredFollowers.map((follower, index) => (
                        <FollowerCard
                          key={follower._id}
                          follower={follower}
                          isSelected={selectedFollowers.has(follower._id)}
                          isMultiSelectMode={isMultiSelectMode}
                          isEditing={editingId === follower._id}
                          editValue={editValue}
                          isRemoving={removingFollowers.has(follower._id)}
                          loadingAction={loadingFollowers.get(follower._id)}
                          activeFilter={activeFilter}
                          onSelect={handleSelectFollower}
                          onEdit={handleEdit}
                          onSave={handleSave}
                          onCancel={handleCancel}
                          onStatusChange={handleStatusChange}
                          inViewRef={inViewRef}
                          isLastItem={index === filteredFollowers.length - 1}
                          hasNextPage={page !== null && page !== 0}
                        />
                      ))}
                    </div>

                    {/* Infinite Scroll Loading Indicator */}
                    {page !== null && page !== 0 && (
                      <div className='flex items-center justify-center py-3'>
                        <Loader2 className='h-4 w-4 animate-spin' />
                      </div>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (followersError && followers.length === 0) {
    return (
      <FollowerErrorState
        error={followersError}
        onRetry={() => fetchData('new', 1)}
      />
    );
  }

  return (
    <div className='flex h-full flex-col p-5'>
      {/* Fixed Filter and Selection Bar */}
      <div className='flex-shrink-0 space-y-4 rounded-lg bg-muted/50 p-4'>
        {/* Top Row: Filters */}
        {activeFilter === 'history' ? (
          <HistoryFilters
            historyDate={historyDate}
            historyActionType={historyActionType}
            historySummary={historySummary}
            onDateChange={setHistoryDate}
            onActionTypeChange={setHistoryActionType}
            onNavigateToPending={() => setActiveFilter('pending')}
            isLoading={followersLoading || filterLoading}
          />
        ) : (
          <FollowerFilters
            activeFilter={activeFilter}
            searchQuery={searchQuery}
            isMultiSelectMode={isMultiSelectMode}
            filteredFollowersLength={filteredFollowers.length}
            totals={totals}
            onFilterChange={setActiveFilter}
            onSearchChange={setSearchQuery}
            onMultiSelectToggle={toggleMultiSelectMode}
            isLoading={followersLoading || filterLoading}
          />
        )}

        {/* Bottom Row: Bulk Actions */}
        <FollowerBulkActions
          isMultiSelectMode={isMultiSelectMode}
          selectedCount={selectedCount}
          totalFollowersCount={followerCount}
          currentPageFollowersCount={filteredFollowers.length}
          bulkLoading={bulkLoading}
          activeFilter={activeFilter}
          onSelectAll={handleSelectAll}
          onBulkApprove={handleBulkApprove}
          onBulkReject={handleBulkReject}
        />
      </div>

      {/* Scrollable Followers List */}
      <div className='mt-4 min-h-0 flex-1'>
        <ScrollArea ref={scrollAreaRef} className='h-full'>
          <div className='pl-1 pr-4 pt-1'>
            {filteredFollowers.length === 0 ? (
              <FollowerEmptyState />
            ) : (
              <>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
                  {filteredFollowers.map((follower, index) => (
                    <FollowerCard
                      key={follower._id}
                      follower={follower}
                      isSelected={selectedFollowers.has(follower._id)}
                      isMultiSelectMode={isMultiSelectMode}
                      isEditing={editingId === follower._id}
                      editValue={editValue}
                      isRemoving={removingFollowers.has(follower._id)}
                      loadingAction={loadingFollowers.get(follower._id)}
                      activeFilter={activeFilter}
                      onSelect={handleSelectFollower}
                      onEdit={handleEdit}
                      onSave={handleSave}
                      onCancel={handleCancel}
                      onStatusChange={handleStatusChange}
                      inViewRef={inViewRef}
                      isLastItem={index === filteredFollowers.length - 1}
                      hasNextPage={page !== null && page !== 0}
                    />
                  ))}
                </div>

                {/* Infinite Scroll Loading Indicator */}
                {page !== null && page !== 0 && (
                  <div className='flex items-center justify-center py-3'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default FollowerList;
